import React, { FC, useCallback, useMemo, useState } from 'react';
import { connect } from 'react-redux';
import { withRouter } from 'react-router';
import { Button, Typography, Select, message, Modal, Spin, Empty } from 'antd';
import {
	downloadLocationsCSVForSupplier,
	locationsRestCrudThunksForSupplier,
} from '../../thunks/locations_thunks';
import { updateFiltersEntities } from '../../thunks/thunks_wrapper';
import Ellipsis from 'ant-design-pro/lib/Ellipsis';
import { EmptyState } from '../empty_state/EmptyState';
import {
	nullSafeGet,
	nullSafeGetOrElse,
	getTupleNameFieldName,
	getTupleValueFieldName,
	isSupplierPayingCustomerAndAdmin,
} from '../../utils/DataAccessUtils';
import { EmptorTag } from '../emptor_tag/EmptorTag';
import { FILTER_CONFIG_NAMES, FILTER_FIELD_TYPE, ROLE_TYPES } from '../../utils/DataConstants';
import { PERMISSION_NAMES } from '../../utils/AuthUtils';
import AccessPermissionChecker from '../common/AccessPermissionChecker';
import OWAsyncTupleSelect from '../ow_async_select/OWAsyncTupleSelect';
import { locationTypesAdditionalFieldsRestCrudThunksForSupplier } from '../../thunks/location_types_additional_fields_thunks';
import { SUPPLIER_ADMIN_ONLY } from '../roles/roles';
import { brandsRestCrudThunksForSupplier } from '../../thunks/brands_thunks';
import { INITIAL_IS_ACTIVE_FILTERS } from '../../utils/FilterUtils';
import ReduxDataTable from '../redux_data_table/ReduxDataTable';
import { getLinkWIthBackLinkParams } from '../../utils/HistoryUtils';
import { DownloadOutlined } from '@ant-design/icons';
import BrandLogoDisplay from '../brands_overview_index_page/BrandLogoDisplay';
import { useFilters } from '../../hooks/useFilters';
import { getLocationFilterConfig } from '../../utils/LocationFilterUtils';
import { buildLocationsMapPopupParts } from '../../utils/LocationsMapPopupUtils';
import MapboxGallery from '../mapboxgallery/MapboxGallery';

require('./SupplierLocationsIndexPage.less');
const { Text } = Typography;

const TC_NAME = 'locationsIndex';
const LOCATIONS_OVERVIEW_TARGET_COLLECTION = 'locationsIndex';
const LOCATIONS_ADVANCED_FILTERS = 'locationsAdvancedFilters';

const SupplierLocationsIndexPage: FC<any> = ({
	history,
	location,
	locations,
	updateFilters,
	currentUser,
	exportLocations,
	fetchLocationTypesAdditionalFields,
	fetchMultipleLocationTypesAdditionalFields,
	locationTypesAdditionalFields,
	fetchBrandsCount,
	fetchAllLocationsLite,
}) => {
	const [exporting, setExporting] = useState(false);
	const [mapModalOpen, setMapModalOpen] = useState(false);
	const [mapModalLoading, setMapModalLoading] = useState(false);
	const [mapModalLocations, setMapModalLocations] = useState<any[]>([]);

	const [brandAvailable, setBrandAvailable] = useState(false);

	React.useEffect(() => {
		fetchBrandsCount()
			.then((count) => setBrandAvailable(count > 0))
			.catch(() => {});
	}, []);

	const locationFilters = useFilters({
		filterTargetCollectionName: LOCATIONS_ADVANCED_FILTERS,
		overviewCollectionName: LOCATIONS_OVERVIEW_TARGET_COLLECTION,
		updateFilters,
	});

	const additionalFieldsFilter = useMemo(
		() => ({
			label: 'Additional Fields',
			fieldName: FILTER_CONFIG_NAMES.ADDITIONAL_FIELD,
			type: FILTER_FIELD_TYPE.TUPLE,
			stateSlice: locationTypesAdditionalFields,
			filtersValueAccessor: (filtersStateSlice) => {
				const name = filtersStateSlice[getTupleNameFieldName(FILTER_CONFIG_NAMES.ADDITIONAL_FIELD)];
				const value =
					filtersStateSlice[getTupleValueFieldName(FILTER_CONFIG_NAMES.ADDITIONAL_FIELD)];
				return [name, value];
			},
			handleChange: (values) => {
				updateFilters({
					[getTupleNameFieldName(FILTER_CONFIG_NAMES.ADDITIONAL_FIELD)]: nullSafeGetOrElse(
						'0',
						values,
						undefined
					),
					[getTupleValueFieldName(FILTER_CONFIG_NAMES.ADDITIONAL_FIELD)]: nullSafeGetOrElse(
						'1',
						values,
						undefined
					),
				});
			},
			targetCollectionName: LOCATIONS_ADVANCED_FILTERS,
			fetch: fetchLocationTypesAdditionalFields,
			fetchMultiple: fetchMultipleLocationTypesAdditionalFields,
			renderRecord: (entity) => (
				<Select.Option key={entity.id} value={entity.id}>
					<div className="flex flex-row items-center">
						<div>{entity.name}</div>
					</div>
				</Select.Option>
			),
			valueAccessor: (item) => item.name,
			component: OWAsyncTupleSelect,
		}),
		[
			locationTypesAdditionalFields,
			updateFilters,
			fetchLocationTypesAdditionalFields,
			fetchMultipleLocationTypesAdditionalFields,
		]
	);

	const filterConfig = useMemo(
		() =>
			getLocationFilterConfig({
				filters: locationFilters,
				brandAvailable,
				additionalFieldsFilter,
				showCustomers: isSupplierPayingCustomerAndAdmin(currentUser),
			}),
		[locationFilters, brandAvailable, additionalFieldsFilter, currentUser]
	);

	const downloadCSV = useCallback(() => {
		const filters = nullSafeGetOrElse(`${TC_NAME}.filters`, locations, {});
		setExporting(true);
		exportLocations(filters)
			.catch((err) => message.error(err))
			.finally(() => setExporting(false));
	}, [locations, exportLocations]);

	const popupRenderFunc = useCallback(
		(record) => buildLocationsMapPopupParts(record, ROLE_TYPES.SUPPLIER),
		[]
	);

	const columns = useMemo(
		() => [
			{
				title: 'Name',
				dataIndex: 'name',
				defaultSortOrder: 'ascend',
				render: (text, record) => (
					<div className="locationsIndexPage__row">
						<div>
							<div className="flex items-center">
								<BrandLogoDisplay brand={nullSafeGet('brand', record)} />
								<div>
									<div className="locationsIndexPage__rowTitle">
										<Ellipsis tooltip={true} length={64}>
											{record.name}
										</Ellipsis>
									</div>
									<div className="locationsIndexPage__rowSubtitle">
										<div className="locationsIndexPage__rowInlineGroup">
											{nullSafeGet('buyerFacility.primaryAddress.city', record)}
										</div>
										<div className="locationsIndexPage__rowInlineGroup2">
											{nullSafeGet('locationType.name', record)} {nullSafeGet('storeId', record)}
											{isSupplierPayingCustomerAndAdmin(currentUser) ? (
												<span className="ml-2">
													| {nullSafeGet('buyerCompany.displayName', record)}
												</span>
											) : null}
										</div>
										{record.isActive ? null : (
											<div className="locationsIndexPage__rowInlineGroup">
												<Text className={'mr-1'} type={'danger'}>
													Inactive
												</Text>
											</div>
										)}
									</div>
								</div>
							</div>
						</div>
						<div className="locationsIndexPage__rightSide">
							<EmptorTag className="locationsIndexPage__pill">
								{record.totalNotClosedWOCount > 0
									? record.totalNotClosedWOCount + ' Open WOs'
									: 'No Open WOs'}
							</EmptorTag>
						</div>
					</div>
				),
			},
		],
		[currentUser]
	);

	const onRow = useCallback(
		(record) => ({
			onClick: () => {
				const path = `/supplier/locations/detail/${nullSafeGet(
					'id',
					record
				)}/details?targetCollectionName=${LOCATIONS_OVERVIEW_TARGET_COLLECTION}`;
				history.push(
					getLinkWIthBackLinkParams(location, 'Back to Assets', path, {
						preserve: true,
					})
				);
			},
		}),
		[history, location]
	);

	const isAdmin = currentUser.roles.some((role) => SUPPLIER_ADMIN_ONLY.has(role));

	const openLocationsMapModal = useCallback(() => {
		setMapModalOpen(true);
		setMapModalLoading(true);
		setMapModalLocations([]);
		fetchAllLocationsLite()
			.then((data) => {
				setMapModalLocations(Array.isArray(data) ? data : []);
			})
			.catch((err) => {
				message.error(typeof err === 'string' ? err : 'Failed to load locations');
				setMapModalOpen(false);
			})
			.finally(() => setMapModalLoading(false));
	}, [fetchAllLocationsLite]);

	const closeLocationsMapModal = useCallback(() => {
		setMapModalOpen(false);
		setMapModalLocations([]);
	}, []);

	const mapGeolocatedEntities = useMemo(
		() =>
			mapModalLocations.filter(
				(record) =>
					nullSafeGet('buyerFacility.longitude', record) &&
					nullSafeGet('buyerFacility.latitude', record)
			),
		[mapModalLocations]
	);

	const longitudeForMap = useCallback((el) => nullSafeGet('buyerFacility.longitude', el), []);
	const latitudeForMap = useCallback((el) => nullSafeGet('buyerFacility.latitude', el), []);

	return (
		<div className="locationsIndexPage">
			<Modal
				title="Locations map"
				visible={mapModalOpen}
				onCancel={closeLocationsMapModal}
				footer={null}
				width={960}
				destroyOnClose
			>
				{mapModalLoading ? (
					<div className="flex min-h-[420px] items-center justify-center">
						<Spin size="large" />
					</div>
				) : mapGeolocatedEntities.length === 0 ? (
					<div className="flex min-h-[420px] items-center justify-center px-4">
						<Empty description="No locations with map coordinates to show." />
					</div>
				) : (
					<div
						className="relative min-h-[420px] w-full overflow-hidden"
						style={{ height: 'min(60vh, 520px)' }}
					>
						<MapboxGallery
							key={mapGeolocatedEntities.length}
							popupRenderFunc={popupRenderFunc}
							geolocatedEntities={mapGeolocatedEntities}
							hideSearchWithinArea={true}
							baseLayerStyle="light"
							longitudeAccessor={longitudeForMap}
							latitudeAccessor={latitudeForMap}
						/>
					</div>
				)}
			</Modal>
			<ReduxDataTable
				filterConfig={filterConfig}
				rightActions={
					<div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
						<Button
							size="large"
							icon={<DownloadOutlined />}
							className="inline-block-visible-md"
							loading={exporting}
							onClick={downloadCSV}
						>
							<span className="inline-block-visible-xl">Export CSV</span>
						</Button>
						<Button.Group size="large">
							<Button disabled={true} title="List view">
								<i className="icons8-font icons8-bulleted-list-2" />
							</Button>
							<Button onClick={openLocationsMapModal} loading={mapModalLoading} title="Map">
								<i className="icons8-font icons8-location-marker" />
							</Button>
						</Button.Group>
						{isAdmin ? (
							<Button
								size="large"
								type="primary"
								onClick={() => history.push('/supplier/locations/overview/new')}
							>
								Add Location
							</Button>
						) : null}
					</div>
				}
				handleQueryParams
				emptyState={
					<EmptyState
						graphic={
							<img
								style={{ marginBottom: 8 }}
								src="https://s3.amazonaws.com/mock-data-assets/categories/images/cactus.svg"
								alt={"It's a little lonely in here."}
							/>
						}
						headline={"It's a little lonely in here."}
						body={
							<div style={{ textAlign: 'center' }}>
								<div style={{ maxWidth: 440, marginBottom: 16 }}>
									Looks like your team has not added any locations yet.
								</div>
								<AccessPermissionChecker name={PERMISSION_NAMES.MODIFY_LOCATIONS}>
									<Button
										type="primary"
										onClick={() => history.push('/supplier/locations/overview/new')}
									>
										Add your first location
									</Button>
								</AccessPermissionChecker>
							</div>
						}
					/>
				}
				entityCollectionName="locations"
				targetCollectionName={TC_NAME}
				tableColumns={columns}
				showHeader={false}
				keyAccessor={(el) => el.id}
				onTableRow={onRow}
				preAppliedFilters={{ ...INITIAL_IS_ACTIVE_FILTERS }}
				fetchData={locationsRestCrudThunksForSupplier}
				initialSorting={{ sort_by: 'name', order: 'ascend' }}
				stateSlice={locations}
			/>
		</div>
	);
};

const mapStateToProps = (state, ownProps) => ({
	history: ownProps.history,
	locations: state.locations,
	currentUser: state.session.currentUser,
	locationTypesAdditionalFields: state.location_types_additional_fields,
});

const mapDispatchToProps = (dispatch, ownProps) => ({
	updateFilters: (filters) =>
		dispatch(
			updateFiltersEntities(
				ROLE_TYPES.SUPPLIER,
				'locations',
				locationsRestCrudThunksForSupplier,
				null
			)(true, ownProps.history, LOCATIONS_OVERVIEW_TARGET_COLLECTION, filters, undefined)
		),
	fetchMultipleLocationTypesAdditionalFields: (ids, targetCollectionName) =>
		dispatch(
			locationTypesAdditionalFieldsRestCrudThunksForSupplier.readMultiple(ids, targetCollectionName)
		),
	fetchLocationTypesAdditionalFields: (
		params,
		targetCollectionName,
		pagination,
		sorting,
		filters,
		addToTargetCollection
	) =>
		dispatch(
			locationTypesAdditionalFieldsRestCrudThunksForSupplier.read(
				params,
				targetCollectionName,
				pagination,
				sorting,
				filters,
				addToTargetCollection
			)
		),
	exportLocations: (params, filters) => dispatch(downloadLocationsCSVForSupplier(params, filters)),
	fetchBrandsCount: (params) => dispatch(brandsRestCrudThunksForSupplier.countBy(params, 'all')),
	fetchAllLocationsLite: () =>
		dispatch(
			locationsRestCrudThunksForSupplier.readLite(
				{ no_pagination: true, noIsActiveFilter: true },
				'__supplierLocationsMapModal__',
				null,
				null,
				null,
				false,
				true
			)
		),
});

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(SupplierLocationsIndexPage));
